import { useState } from 'react';

import { api, type InboxNotification } from '../live/api.ts';
import { useLive, useStore } from '../live/context.tsx';
import { useTimeFormat } from '../lib/display.ts';
import { absoluteTime, observedTime } from '../lib/format.ts';
import { BellIcon } from './icons.tsx';

/** Operator alerts (`alert.*`) are about a machine or the backups; they open a view, not a ticket. */
function alertView(kind: string): 'agents' | 'ops' | null {
  if (kind === 'alert.harness-login' || kind === 'alert.runner-offline') return 'agents';
  if (kind.startsWith('alert.')) return 'ops';
  return null;
}

function kindKicker(kind: string): string {
  if (kind.startsWith('alert.')) return 'Alert';
  if (kind === 'mention') return 'Mention';
  if (kind === 'human_review') return 'Human review';
  if (kind.startsWith('waiting.')) {
    const reason = kind.slice('waiting.'.length).replace(/_/g, ' ');
    return reason.charAt(0).toUpperCase() + reason.slice(1);
  }
  return kind;
}

export function NotificationCentre({
  onOpenTicket,
  onOpenView,
}: {
  onOpenTicket: (key: string, run?: number, project?: string | null) => void;
  onOpenView: (view: 'agents' | 'ops') => void;
}) {
  const store = useStore();
  const state = useLive();
  const format = useTimeFormat();
  const inbox = state.inbox;
  const unread = inbox?.unreadCount ?? 0;
  const [open, setOpen] = useState(false);

  async function markRead(item: InboxNotification): Promise<void> {
    if (item.readAt == null) {
      await api.markNotificationRead(item.id).catch(() => undefined);
    }
    void store.refetch();
  }

  return (
    <div className="notify-centre">
      <button
        type="button"
        className={`btn btn-ghost btn-sm notify-bell${unread > 0 ? ' has-unread' : ''}`}
        aria-label={unread > 0 ? `Notifications, ${unread} unread` : 'Notifications'}
        aria-haspopup="dialog"
        aria-expanded={open}
        title={unread > 0 ? `${unread} unread` : 'Notifications'}
        onClick={() => setOpen((value) => !value)}
      >
        <BellIcon size={14} />
        {unread > 0 ? (
          <span className="notify-count num" aria-hidden>
            {unread > 99 ? '99+' : unread}
          </span>
        ) : null}
      </button>
      {open ? (
        <>
          <div
            className="notify-scrim"
            onClick={() => setOpen(false)}
            role="presentation"
          />
          <div className="menu notify-panel" role="dialog" aria-label="Notifications">
            <div className="notify-head">
              <span className="kicker">Notifications</span>
              {unread > 0 ? (
                <button
                  type="button"
                  className="btn btn-ghost btn-sm"
                  onClick={() => {
                    void api.markAllNotificationsRead().then(() => store.refetch());
                  }}
                >
                  Mark all read
                </button>
              ) : null}
            </div>
            {(inbox?.notifications.length ?? 0) === 0 ? (
              <p className="notify-empty">Nothing needs you.</p>
            ) : (
              <div className="notify-list">
                {(inbox?.notifications ?? []).map((item) => {
                  const unreadItem = item.readAt == null;
                  return (
                    <button
                      key={item.id}
                      type="button"
                      className={`menu-item notify-item${unreadItem ? ' is-unread' : ''}`}
                      onClick={() => {
                        void markRead(item);
                        const view = alertView(item.kind);
                        if (view) onOpenView(view);
                        else if (item.ref) onOpenTicket(item.ref, undefined, item.projectSlug);
                        setOpen(false);
                      }}
                    >
                      <span
                        className={`lamp${unreadItem ? ' lamp-breathe' : ''}`}
                        style={unreadItem ? { background: 'var(--accent)' } : undefined}
                        aria-hidden
                      />
                      <span className="notify-item-copy">
                        <span className="notify-item-title">{item.title}</span>
                        <span className="notify-item-meta">
                          <span>{kindKicker(item.kind)}</span>
                          {item.ref && !alertView(item.kind) ? <span className="mono">{item.ref}</span> : null}
                          <span className="dim" title={absoluteTime(item.createdAt)}>
                            {observedTime(item.createdAt, state.tick, format)}
                          </span>
                        </span>
                      </span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </>
      ) : null}
    </div>
  );
}
